/**
 * Orders API – mirrors src/services/orders.ts and routes/orders.ts in
 * vin-pulse-os-api.
 *
 * Customer endpoints used here:
 *   POST   /api/v1/orders                – create (Customer-mode auto-assigns customer_id from JWT)
 *   GET    /api/v1/customers/me/orders   – paginated history with filters
 *   GET    /api/v1/orders/:id            – single order detail
 *   DELETE /api/v1/orders/:id            – cancel pending order
 *
 * Backend wraps GET /customers/me/orders as `{ success, data: { orders, total, ... } }`.
 * apiClient unwraps the outer `data`, then `normalizeListResponse` flattens
 * the inner shape so callers always see `{ data, total, limit, offset }`.
 */

import { apiClient } from './apiClient';

export type OrderStatus =
  | 'pending'
  | 'confirmed'
  | 'shipped'
  | 'delivered'
  | 'invoiced'
  | 'cancelled';

export interface OrderItem {
  id?: number;
  sku_id: number;
  sku_code?: string;
  product_name?: string;
  quantity: number;
  unit_price: number;
  line_total?: number;
  tax_amount?: number;
}

export interface Order {
  id: number;
  customer_id: number;
  customer_name?: string;
  order_date: string;
  status: OrderStatus;
  total_amount: number;
  tax_total?: number;
  items?: OrderItem[];
  shipping_address_id?: number | null;
  created_by_user_id?: number | null;
  created_at: string;
  updated_at: string;
  confirmed_at?: string | null;
  shipped_at?: string | null;
  delivered_at?: string | null;
  cancelled_at?: string | null;
}

export interface ListOrdersFilters {
  status?: OrderStatus;
  startDate?: string;
  endDate?: string;
  sortBy?: 'created_at' | 'total_amount' | 'status';
  sortOrder?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export interface CreateOrderItemInput {
  sku_id: number;
  quantity: number;
  unit_price: number;
}

export interface CustomerCreateOrderInput {
  shipping_address_id: number;
  items: CreateOrderItemInput[];
}

interface OrderListEnvelope {
  data: Order[];
  total: number;
  limit: number;
  offset: number;
}

function normalizeListResponse(raw: any): OrderListEnvelope {
  // After apiClient.get unwraps `{ success, data: {...} }`, raw is the inner
  // object. Some endpoints also return un-wrapped lists – handle both.
  const inner = raw?.orders !== undefined ? raw : raw?.data ?? raw;
  return {
    data: inner?.orders ?? [],
    total: inner?.total ?? 0,
    limit: inner?.limit ?? 20,
    offset: inner?.offset ?? 0,
  };
}

/**
 * Validate a customer-created order request before sending. Rejects with
 * the same error codes the backend would (so frontend & backend agree on
 * the failure modes).
 */
function validateCreateOrder(input: CustomerCreateOrderInput): void {
  if (
    !input.shipping_address_id ||
    !Number.isInteger(input.shipping_address_id) ||
    input.shipping_address_id <= 0
  ) {
    throw new Error('shipping_address_id is required and must be a positive integer');
  }
  if (!Array.isArray(input.items) || input.items.length === 0) {
    throw new Error('items array must contain at least one item');
  }
  for (let i = 0; i < input.items.length; i++) {
    const item = input.items[i];
    if (!Number.isInteger(item.sku_id) || item.sku_id <= 0) {
      throw new Error(`Item ${i}: sku_id is required and must be a positive integer`);
    }
    if (!Number.isInteger(item.quantity) || item.quantity <= 0) {
      throw new Error(`Item ${i}: quantity is required and must be a positive integer`);
    }
    if (typeof item.unit_price !== 'number' || item.unit_price < 0) {
      throw new Error(`Item ${i}: unit_price is required and must be a non-negative number`);
    }
  }
}

export async function createCustomerOrder(input: CustomerCreateOrderInput): Promise<Order> {
  validateCreateOrder(input);
  return apiClient.post<Order>('/orders', input);
}

export async function listMyOrders(filters: ListOrdersFilters = {}): Promise<OrderListEnvelope> {
  const raw = await apiClient.get<any>(
    '/customers/me/orders',
    filters as Record<string, any>
  );
  return normalizeListResponse(raw);
}

export async function getOrder(id: number | string): Promise<Order> {
  return apiClient.get<Order>(`/orders/${id}`);
}

export interface OrderStatusHistoryEntry {
  from_status: string | null;
  to_status: string;
  changed_at: string;
  changed_by_user_id: number | null;
  reason: string | null;
}

/**
 * GET /api/v1/orders/:id/status-history — the order's status timeline.
 * Customer-allowed for their own orders. Returns entries oldest→newest.
 */
export async function getOrderStatusHistory(
  id: number | string
): Promise<OrderStatusHistoryEntry[]> {
  const raw = await apiClient.get<any>(`/orders/${id}/status-history`);
  // apiClient unwraps `{ success, data }`; the payload is
  // `{ order_id, status_history, count }` (or already the inner object).
  return raw?.status_history ?? raw?.data?.status_history ?? [];
}

export interface SuggestedReorderLine {
  sku_id: number;
  sku_code?: string;
  product_name?: string;
  avg_quantity?: number;
  [key: string]: any;
}

/**
 * GET /api/v1/orders/customers/:customerId/suggested-reorder — average
 * quantity per SKU from the customer's recent order history (VP-SALES-004).
 * For the Customer role the backend ignores :customerId and always resolves
 * to the caller's own id, so any placeholder value works here. Used by the
 * catalog's "Previously Ordered" quick filter (VP-CUST-002) since
 * GET /customers/me/orders only returns an item_count per order, not real
 * line items.
 */
export async function getSuggestedReorder(
  customerId: number | string = 'me'
): Promise<SuggestedReorderLine[]> {
  const raw = await apiClient.get<any>(`/orders/customers/${customerId}/suggested-reorder`);
  return Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
}

export async function cancelOrder(id: number | string): Promise<{
  order_id: number;
  previous_status: OrderStatus;
  new_status: 'cancelled';
  cancelled_at: string;
  inventory_released_count: number;
}> {
  return apiClient.delete(`/orders/${id}`);
}
